import type { AuthTenant } from '@/types/tenant';
import {
  extractTenantSlugFromPath,
  resolveTenantBySlug,
} from '@/utils/active-tenant';

const POST_LOGIN_RETURN_PATH_KEY = 'lift-post-login-return-path';

/** Set once this page load has shown the app to a signed-in user. */
let hasEnteredApp = false;

/** In-app paths only — never "//host" or "/\host", which browsers treat as another origin. */
function isSafeInAppPath(path: string): boolean {
  return (
    path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\')
  );
}

/** Reload or Back/Forward re-open a page the user already had, not a link. */
function isReloadOrHistoryNavigation(): boolean {
  try {
    const [entry] = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    return entry?.type === 'reload' || entry?.type === 'back_forward';
  } catch {
    return false;
  }
}

/**
 * Remembers the in-app URL a signed-out visitor opened (e.g. a practice link
 * from an email) so login can send them back to it. Kept in sessionStorage so
 * it survives the SSO popup, the Teams reload and Auth0 redirects in this tab.
 *
 * Only a freshly opened link counts: not a page reached after logout or a
 * mid-session expiry (Back, a reload, or this page load already showed the
 * app), so those keep landing on Home as before.
 */
export function markPostLoginReturnPath(path: string): void {
  if (!isSafeInAppPath(path)) return;
  if (hasEnteredApp || isReloadOrHistoryNavigation()) return;

  try {
    sessionStorage.setItem(POST_LOGIN_RETURN_PATH_KEY, path);
  } catch {
    // Storage unavailable: login falls back to Home.
  }
}

/**
 * The remembered path, when its tenant is one of the user's. Read-only: every
 * post-login navigator (login form, tenant selector, SSO, PublicRoutes) may ask,
 * and the path is cleared once the user reaches the app.
 */
export function getPostLoginReturnPath(
  tenants: AuthTenant[] | null | undefined
): string | null {
  let path: string | null = null;
  try {
    path = sessionStorage.getItem(POST_LOGIN_RETURN_PATH_KEY);
  } catch {
    return null;
  }

  if (!path || !isSafeInAppPath(path)) return null;

  const { pathname } = new URL(path, window.location.origin);
  // A bare "/{tenant}" has no page of its own; Home is the better landing.
  if (pathname.split('/').filter(Boolean).length < 2) return null;

  const slug = extractTenantSlugFromPath(pathname);
  return resolveTenantBySlug(slug, tenants) ? path : null;
}

export function clearPostLoginReturnPath(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_RETURN_PATH_KEY);
  } catch {
    // Ignore.
  }
}

/** The user reached the app: the remembered link is used up. */
export function settlePostLoginReturnPath(): void {
  hasEnteredApp = true;
  clearPostLoginReturnPath();
}
